import { type ReactNode } from 'react'
import { useNavigate } from 'react-router'

import type { HospitalListItem } from '../../api/hospitals'
import { bedTone } from '../../lib/hospitalCard'
import { BED_TYPE_DEFS } from '../../lib/bedTypes'
import { BedBadge } from '../BedBadge'
import { Icon } from '../Icon'

/**
 * Beds tab (M12-T03, ADR-0027). Live per-type counts; tapping a type opens
 * `/book/:slug?type=…` (the T02 deep link) — that is where a bed price first
 * appears, attached to the chosen type. No price on this tab. A type with none
 * free is disabled (immediate-availability model — you can't book what's full).
 */
export function BedsTab({
  hospital,
}: {
  hospital: HospitalListItem
}): ReactNode {
  const navigate = useNavigate()
  return (
    <div className="px-4 py-4">
      <p className="mb-2 font-mono text-[10px] uppercase tracking-wide text-ink-3">
        Tap a bed type to see its price &amp; book
      </p>
      <ul className="flex flex-col gap-2">
        {BED_TYPE_DEFS.map((def) => {
          const free = hospital.beds[def.free]
          const total = hospital.beds[def.total]
          const tone = bedTone(free, total)
          const full = free === 0
          return (
            <li key={def.key}>
              <button
                type="button"
                disabled={full}
                onClick={() =>
                  navigate(`/book/${hospital.slug}?type=${def.key}`)
                }
                className="flex w-full items-center gap-3 rounded-r3 border border-line-1 bg-bg-app px-3.5 py-3 text-left transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-card focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:shadow-none"
              >
                <span className="flex-1 text-sm font-semibold text-ink-1">
                  {def.label}
                </span>
                <BedBadge
                  tone={tone}
                  label={full ? 'Full' : 'Free'}
                  free={free}
                  total={total}
                />
                {!full && (
                  <Icon name="chevr" size={14} className="text-ink-3" />
                )}
              </button>
            </li>
          )
        })}
      </ul>
      <p className="mt-3 text-[12px] text-ink-3">
        Bed prices appear on the next step, once you pick a type.
      </p>
    </div>
  )
}
